import { useEffect, useState, useMemo } from "react";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Download, Lock, BookOpen, Presentation, FileType, FolderOpen } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";
import { PdfViewerModal } from "@/components/PdfViewerModal";
import { openPdf, isNativeApp } from "@/lib/native-pdf";

const typeIcon = (t) => {
  if (t === "apostila") return BookOpen;
  if (t === "slide") return Presentation;
  if (t === "pdf") return FileText;
  return FileType;
};

const typeLabel = (t) => {
  if (t === "apostila") return "Apostila";
  if (t === "slide") return "Apresentação";
  if (t === "pdf") return "PDF";
  return "Outro";
};

export default function Materiais() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [tab, setTab] = useState(user?.profile === "licitacoes" ? "licitacoes" : "legislativo");
  const [courseFilter, setCourseFilter] = useState("all");
  const [downloading, setDownloading] = useState(null);
  const [viewerMaterial, setViewerMaterial] = useState(null);

  // Carrega cursos para o filtro
  useEffect(() => {
    api.get("/courses").then(r => setCourses(r.data || [])).catch(() => {});
  }, []);

  // Carrega materiais
  useEffect(() => {
    const params = {};
    if (user?.profile !== "ambos") params.area = user?.profile;
    else params.area = tab;
    api.get("/materials", { params }).then(r => setItems(r.data || [])).catch(() => {});
  }, [user?.profile, tab]);

  // Lista filtrada por curso selecionado
  const visibleItems = useMemo(() => {
    if (courseFilter === "all") return items;
    if (courseFilter === "none") return items.filter(m => !m.course_id);
    return items.filter(m => m.course_id === courseFilter);
  }, [items, courseFilter]);

  // Cursos que tem material vinculado (pra montar select limpo)
  const coursesWithMaterials = useMemo(() => {
    const ids = new Set(items.filter(m => m.course_id).map(m => m.course_id));
    return courses.filter(c => ids.has(c.id));
  }, [items, courses]);

  /**
   * Abre o material PDF.
   *
   * Estratégia (ago/2026):
   * - **App nativo (Capacitor iOS/Android)**: `openPdf()` salva em Cache e
   *   abre no visualizador nativo (QuickLook / Intent). Antes o app apenas
   *   silenciava — `<a download>` é ignorado pelo WebView.
   * - **Web (desktop, mobile browser, PWA)**: abre `PdfViewerModal` — mesmo
   *   componente da tela de certificado, com botões Baixar/Compartilhar
   *   no topo. Sem popup blocker, sem tela branca no celular.
   * - **file_url externo** (link YouTube, Drive, etc.): `window.open()`
   *   síncrono ao click = não é bloqueado.
   */
  const onDownload = async (m) => {
    if (m._locked) {
      toast.error("Conteúdo exclusivo para alunos validados.");
      return;
    }
    if (!m.has_file && !m.file_url) {
      toast.error("Arquivo indisponível.");
      return;
    }

    // Material com link externo — abre direto (sem passar pelo backend)
    if (!m.has_file && m.file_url) {
      window.open(m.file_url, "_blank", "noopener,noreferrer");
      return;
    }

    // App nativo — usa o visualizador nativo do SO
    if (isNativeApp()) {
      setDownloading(m.id);
      try {
        const token = localStorage.getItem("ceap_token");
        const res = await fetch(`${API}/materials/${m.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error(res.status === 403 ? "sem acesso" : "erro");
        }
        const blob = await res.blob();
        if (!blob || blob.size < 512) {
          toast.error("Arquivo indisponível.");
          return;
        }
        const filename = m.file_name || `${m.title || "material"}.pdf`;
        await openPdf(blob, filename);
      } catch (err) {
        toast.error(err?.message === "sem acesso"
          ? "Conteúdo exclusivo dos alunos do curso"
          : "Erro ao baixar");
      } finally {
        setDownloading(null);
      }
      return;
    }

    // Web — abre o modal in-app (mesmo dos certificados)
    setViewerMaterial(m);
  };

  // Fetch function passada para o modal (respeita o gate de acesso)
  const buildFetchFn = (m) => async (signal) => {
    const token = localStorage.getItem("ceap_token");
    return await fetch(`${API}/materials/${m.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
  };

  return (
    <div className="space-y-4" data-testid="materiais-page">
      <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Materiais</h1>
      <p className="text-sm text-muted-foreground">Apostilas, apresentações e PDFs dos cursos em que você participou.</p>

      {user?.profile === "ambos" && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="legislativo">Legislativo</TabsTrigger>
            <TabsTrigger value="licitacoes">Licitações</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Filtro por curso */}
      {coursesWithMaterials.length > 0 && (
        <div data-testid="materiais-course-filter">
          <Select value={courseFilter} onValueChange={setCourseFilter}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filtrar por curso" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os materiais</SelectItem>
              <SelectItem value="none">Material geral (sem curso vinculado)</SelectItem>
              {coursesWithMaterials.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {visibleItems.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Nenhum material por aqui ainda" description="Quando você participar de um curso, os materiais dele aparecerão aqui." />
      ) : (
        visibleItems.map(m => {
          const Icon = typeIcon(m.type);
          const courseTitle = courses.find(c => c.id === m.course_id)?.title;
          const disabled = m._locked || (!m.has_file && !m.file_url);
          return (
            <Card key={m.id} className="rounded-xl" data-testid={`material-${m.id}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--secondary))] text-[hsl(var(--primary))] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="capitalize">{typeLabel(m.type)}</Badge>
                    {m.requires_validation && (
                      <Badge className="bg-[hsl(var(--gold)/0.2)] text-[hsl(var(--gold-foreground))] border-[hsl(var(--gold)/0.45)] flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Aluno validado
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-display font-semibold text-[hsl(var(--primary))] mt-1">{m.title}</h3>
                  {courseTitle && (
                    <p className="text-[11px] text-[hsl(var(--accent))] mt-0.5 truncate" title={courseTitle}>
                      📚 {courseTitle}
                    </p>
                  )}
                  {m.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{m.description}</p>
                  )}
                  <Button
                    size="sm"
                    className="mt-3"
                    onClick={() => onDownload(m)}
                    disabled={disabled || downloading === m.id}
                    data-testid={`material-download-${m.id}`}
                  >
                    {m._locked ? (
                      <><Lock className="w-4 h-4 mr-1" /> Bloqueado</>
                    ) : !m.has_file && !m.file_url ? (
                      <>Indisponível</>
                    ) : downloading === m.id ? (
                      <>Abrindo…</>
                    ) : (
                      <><Download className="w-4 h-4 mr-1" /> Abrir / Baixar</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Modal in-app do PDF — usado no fluxo WEB (mobile/desktop/PWA).
          Substitui window.open que era bloqueado por popup blocker. */}
      <PdfViewerModal
        open={!!viewerMaterial}
        onClose={() => setViewerMaterial(null)}
        title={viewerMaterial ? viewerMaterial.title : ""}
        courseName={viewerMaterial ? (courses.find(c => c.id === viewerMaterial.course_id)?.title || viewerMaterial.title) : ""}
        filename={viewerMaterial ? (viewerMaterial.file_name || `${viewerMaterial.title}.pdf`) : "material.pdf"}
        fetchFn={viewerMaterial ? buildFetchFn(viewerMaterial) : null}
      />
    </div>
  );
}
